import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

// Password strength: returns 0–4
function passwordStrength(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8)  score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw))   score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(4, score);
}

const STRENGTH_LABELS = ['', 'Weak', 'Fair', 'Good', 'Strong'];
const STRENGTH_COLORS = ['', '#ef4444', '#f97316', '#eab308', '#22c55e'];

function EyeIcon()    { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>; }
function EyeOffIcon() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>; }

export default function ResetPassword() {
  const navigate = useNavigate();

  // Supabase fires PASSWORD_RECOVERY when the user arrives via the reset email link.
  // We wait for that event before showing the form.
  const [ready, setReady]           = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);

  // Form state
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw]                   = useState(false);
  const [showConfirm, setShowConfirm]         = useState(false);
  const [submitting, setSubmitting]           = useState(false);
  const [formError, setFormError]             = useState('');
  const [done, setDone]                       = useState(false);
  const [redirectSecs, setRedirectSecs]       = useState(5);

  useEffect(() => {
    // Supabase parses the access_token from the URL fragment and fires PASSWORD_RECOVERY
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setReady(true);
      }
    });
    // If no event fires within 5 seconds, the link is invalid/expired
    const timer = setTimeout(() => {
      setReady(r => { if (!r) setInvalidLink(true); return r; });
    }, 5000);
    return () => { subscription.unsubscribe(); clearTimeout(timer); };
  }, []);

  // Auto-redirect after success
  useEffect(() => {
    if (!done) return;
    if (redirectSecs <= 0) { navigate('/login?reset=1'); return; }
    const t = setTimeout(() => setRedirectSecs(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [done, redirectSecs, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (newPassword.length < 8) { setFormError('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setFormError("Passwords don't match."); return; }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) { setFormError(error.message); return; }
      setDone(true);
    } catch {
      setFormError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const strength  = passwordStrength(newPassword);
  const matchOk   = confirmPassword.length > 0 && newPassword === confirmPassword;
  const matchFail = confirmPassword.length > 0 && newPassword !== confirmPassword;

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 26, fontWeight: 900, color: 'var(--cb-navy)', letterSpacing: '-0.5px' }}>
            Ranked<span style={{ color: 'var(--cb-blue)' }}>SAT</span>
          </div>
        </div>

        {/* Loading / waiting for PASSWORD_RECOVERY event */}
        {!ready && !invalidLink && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div className="queue-spinner" style={{ margin: '0 auto' }} />
            <p style={{ marginTop: 14, color: 'var(--cb-gray-500)', fontSize: 14 }}>Verifying your reset link…</p>
          </div>
        )}

        {/* Invalid link */}
        {invalidLink && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--cb-navy)', marginBottom: 8 }}>Link Expired or Invalid</div>
            <p style={{ fontSize: 14, color: 'var(--cb-gray-500)', lineHeight: 1.6, marginBottom: 24 }}>
              This reset link has expired or already been used. Request a new one.
            </p>
            <Link to="/forgot-password" className="btn btn-primary" style={{ display: 'block', width: '100%', textAlign: 'center', marginBottom: 12 }}>
              Request a New Link
            </Link>
            <Link to="/login" style={{ fontSize: 14, color: 'var(--cb-blue)', fontWeight: 600, textDecoration: 'none' }}>
              Back to Login
            </Link>
          </div>
        )}

        {/* Success */}
        {done && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#f0fdf4', border: '2px solid var(--cb-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--cb-green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--cb-navy)', marginBottom: 8 }}>Password updated!</div>
            <p style={{ fontSize: 14, color: 'var(--cb-gray-500)', lineHeight: 1.6, marginBottom: 24 }}>
              You can now log in with your new password.
            </p>
            <Link to="/login?reset=1" className="btn btn-primary" style={{ display: 'block', width: '100%', textAlign: 'center', marginBottom: 10 }}>
              Log In Now
            </Link>
            <div style={{ fontSize: 13, color: 'var(--cb-gray-400)' }}>Redirecting in {redirectSecs}s…</div>
          </div>
        )}

        {/* Reset form */}
        {ready && !done && (
          <>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--cb-navy)', marginBottom: 6 }}>Set a new password</div>
            </div>

            <form className="email-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">New password</label>
                <div className="pw-input-wrap">
                  <input type={showPw ? 'text' : 'password'} className="form-input" placeholder="At least 8 characters"
                    value={newPassword} onChange={e => setNewPassword(e.target.value)} autoFocus autoComplete="new-password" required />
                  <button type="button" className="pw-toggle" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
                    {showPw ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {newPassword.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                      {[1,2,3,4].map(n => (
                        <div key={n} style={{ flex: 1, height: 4, borderRadius: 2, background: n <= strength ? STRENGTH_COLORS[strength] : 'var(--cb-gray-200)', transition: 'background 0.2s' }} />
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: STRENGTH_COLORS[strength] || 'var(--cb-gray-400)', fontWeight: 600 }}>{STRENGTH_LABELS[strength]}</div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Confirm new password</label>
                <div className="pw-input-wrap">
                  <input type={showConfirm ? 'text' : 'password'} className="form-input" placeholder="Re-enter your new password"
                    value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" required
                    style={{ borderColor: matchFail ? '#fca5a5' : matchOk ? 'var(--cb-green)' : undefined }} />
                  <button type="button" className="pw-toggle" onClick={() => setShowConfirm(v => !v)} tabIndex={-1}>
                    {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {matchFail && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>Passwords don't match</div>}
                {matchOk   && <div style={{ fontSize: 12, color: 'var(--cb-green)', marginTop: 4 }}>✓ Passwords match</div>}
              </div>

              {formError && <div className="form-error" style={{ marginBottom: 12 }}>{formError}</div>}

              <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 4 }}
                disabled={submitting || !newPassword || !confirmPassword}>
                {submitting ? 'Saving…' : 'Set New Password'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 20, fontSize: 14 }}>
              <Link to="/login" style={{ color: 'var(--cb-blue)', fontWeight: 600, textDecoration: 'none' }}>← Back to Login</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
